import { useState } from "react"
import { JSONSchema7 } from "json-schema"
import { ButtonGroup, Image, OutlinedButton, Title } from "dash-styled"
import { ReactComponent as AddIcon } from "icons/lib/plus.svg"
import { ReactComponent as ThreeDocumentsIcon } from "icons/lib/three-documents.svg"

import { DocumentType } from "../../../../../../Models/DataProgram/DocumentExtraction"
import { useDebounce } from "../../../../../../Hooks/useDebounce"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { TabButton } from "../../../../Button/TabButton"
import { DocumentModal } from "../../Common/Modals/DocumentModal"

type DocumentTemplate = {
    image: string
    name: string
}

type DocumentTemplatesCategory = {
    name: string
    items: DocumentTemplate[]
}

type DocumentEditProps = {
    templates: DocumentTemplatesCategory[]
    schema: JSONSchema7
    onSubmit: (value: DocumentType) => void
    onClose: () => void
    value?: DocumentType
}

export const DocumentEdit = ({
    templates,
    schema,
    onSubmit,
    onClose,
    value
}: DocumentEditProps) => {
    const [isHintHovered, setHintEnter, setHintLeave] = useBoolean(false)
    const debouncedHintHovered = useDebounce(isHintHovered, 100)

    const [isTemplatesView, openTemplatesView, closeTemplatesView] = useBoolean(
        !value
    )

    const [selectedCategory, setSelectedCategory] =
        useState<DocumentTemplatesCategory>(templates?.[0])

    const handleOpen = () => {
        closeTemplatesView()
    }

    const handleClose = () => {
        if (value) {
            onClose()
        } else {
            openTemplatesView()
        }
    }

    const handleSubmit = (doc: DocumentType) => {
        onSubmit(doc)
        onClose()
    }

    const hasTemplates = templates.length > 0

    return isTemplatesView ? (
        <div className="flex flex-col p-2.5 max-h-full relative">
            <OutlinedButton
                Icon={AddIcon}
                label={hasTemplates ? "New Document" : "Add new document"}
                className={`self-end py-3 z-5 ${
                    hasTemplates && !debouncedHintHovered
                        ? "bg-white border-gray-500"
                        : "text-white bg-grape-500"
                }`}
                onClick={handleOpen}
            />
            <div className="flex flex-col justify-center items-center my-12">
                <ThreeDocumentsIcon />
                <Title
                    variant="heading2"
                    weight="semibold"
                    className="mt-8 mb-6"
                >
                    {hasTemplates
                        ? "Choose a premade template"
                        : "Create a new document"}
                </Title>
                <div className="text-center z-5">
                    {hasTemplates ? (
                        <div
                            onMouseEnter={setHintEnter}
                            onMouseLeave={setHintLeave}
                        >
                            <p>
                                See nothing you like here? Want to start from
                                scratch?
                            </p>
                            <p>
                                Click{" "}
                                <span className="font-semibold">
                                    New Document
                                </span>{" "}
                                instead
                            </p>
                        </div>
                    ) : (
                        <p>
                            You can choose from premade documents or create a
                            custom document from scratch
                        </p>
                    )}
                </div>
                {debouncedHintHovered && (
                    <div className="absolute top-0 left-0 z-1 w-full h-full bg-white opacity-87" />
                )}
            </div>
            {hasTemplates && (
                <>
                    <ButtonGroup equalWidth>
                        {templates.map(template => (
                            <TabButton
                                key={template.name}
                                onClick={() => setSelectedCategory(template)}
                                active={
                                    selectedCategory?.name === template.name
                                }
                                className="py-2.5"
                            >
                                {template.name}
                            </TabButton>
                        ))}
                    </ButtonGroup>
                    <div className="grid grid-cols-3 gap-4 mt-7.5 overflow-y-auto">
                        {selectedCategory &&
                            selectedCategory.items.map(({ image, name }) => (
                                <div
                                    key={`${selectedCategory.name}_${name}`}
                                    onClick={() => {
                                        handleSubmit({
                                            // TODO: remove hardcode, should be integrated to the backend API
                                            documentId: "id",
                                            documentType: "type",
                                            annotationFields: [],
                                            presetFields: []
                                        })
                                    }}
                                    className="col-span-1 flex flex-col items-center justify-center p-4 border border-gray-500 rounded-lg cursor-pointer"
                                >
                                    <Image imageURL={image} />
                                    <p className="font-semibold uppercase">
                                        {name}
                                    </p>
                                </div>
                            ))}
                    </div>
                </>
            )}
        </div>
    ) : (
        <DocumentModal
            mode="paper"
            onClose={handleClose}
            onSubmit={handleSubmit}
            schema={schema}
            value={value}
        />
    )
}
